import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';
import TranslyChameleon from './TranslyChameleon';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  LayoutDashboard,
  Package,
  FolderOpen,
  FileText,
  Newspaper,
  Image as ImageIcon,
  Menu,
  Zap,
  ListOrdered,
  History as HistoryIcon,
  Settings,
  Activity,
  LogOut,
  User,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
  Database,
  Workflow,
  MessageSquareWarning,
  Inbox as InboxIcon,
  Trophy,
  Tag,
  Tags,
  Boxes,
  Brush,
  Filter,
  CalendarClock,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible';

const SECTIONS = [
  { type: 'item', path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },

  { type: 'label', label: 'Content' },
  { type: 'item', path: '/products', label: 'Products', icon: Package },
  { type: 'item', path: '/collections', label: 'Collections', icon: FolderOpen },
  { type: 'item', path: '/pages-content', label: 'Pages', icon: FileText },
  { type: 'item', path: '/articles', label: 'Articles', icon: Newspaper },
  { type: 'item', path: '/menus', label: 'Menus', icon: Menu },
  { type: 'item', path: '/image-alts', label: 'Image alt-text', icon: ImageIcon },

  {
    type: 'group',
    key: 'custom-data',
    label: 'Custom data',
    icon: Database,
    items: [
      { path: '/metafields/product', label: 'Product metafields', icon: Tag },
      { path: '/metafields/variant', label: 'Variant metafields', icon: Tags },
      { path: '/metafields/collection', label: 'Collection metafields', icon: Tag },
      { path: '/metaobjects', label: 'Metaobjects', icon: Boxes },
      { path: '/theme-strings', label: 'Theme strings', icon: Brush },
      { path: '/filter-labels', label: 'Filter labels', icon: Filter },
    ],
  },

  {
    type: 'group',
    key: 'pipeline',
    label: 'Pipeline',
    icon: Workflow,
    items: [
      { path: '/bulk', label: 'Bulk translate', icon: Zap },
      { path: '/queue', label: 'Queue', icon: ListOrdered },
      { path: '/history', label: 'History', icon: HistoryIcon },
      { path: '/daily-runs', label: 'Daily runs', icon: CalendarClock },
    ],
  },

  {
    type: 'group',
    key: 'feedback',
    label: 'Feedback',
    icon: MessageSquareWarning,
    items: [
      { path: '/feedback/inbox', label: 'Inbox', icon: InboxIcon },
      { path: '/feedback/leaderboard', label: 'Leaderboard', icon: Trophy },
    ],
  },
];

const OPEN_GROUPS_KEY = 'sidebar-open-groups';

function loadOpenGroups() {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    return raw ? JSON.parse(raw) : { 'custom-data': false, pipeline: false, feedback: false };
  } catch {
    return { 'custom-data': false, pipeline: false, feedback: false };
  }
}

export default function Sidebar({ collapsed, setCollapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });

  const [openGroups, setOpenGroups] = useState(loadOpenGroups);

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  useEffect(() => {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
  }, [openGroups]);

  const setGroupOpen = (key, val) => setOpenGroups((prev) => ({ ...prev, [key]: val }));

  const getInitials = (name) => (name ? name.slice(0, 2).toUpperCase() : 'U');

  const itemClass = ({ isActive }) =>
    `flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    }`;

  const subItemClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    }`;

  return (
    <aside className={`fixed left-0 top-0 h-full ${collapsed ? 'w-[68px]' : 'w-56'} border-r border-border bg-card/90 backdrop-blur flex flex-col z-30 transition-all duration-200`}>
      {/* Header */}
      <div className="px-3 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          {collapsed ? (
            <button
              onClick={() => setCollapsed(false)}
              className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent mx-auto"
            >
              <PanelLeftOpen className="h-4 w-4 text-foreground" />
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/dashboard')}>
                <TranslyChameleon size={55} />
                <p className="text-sm font-semibold text-foreground">Transly</p>
              </div>
              <button
                onClick={() => setCollapsed(true)}
                className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
              >
                <PanelLeftClose className="h-4 w-4 text-muted-foreground" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 overflow-y-auto space-y-0.5">
        {SECTIONS.map((section, idx) => {
          if (section.type === 'item') {
            const Icon = section.icon;
            return (
              <NavLink
                key={section.path}
                to={section.path}
                title={collapsed ? section.label : undefined}
                className={itemClass}
              >
                <Icon className="h-4 w-4 shrink-0" />
                {!collapsed && <span>{section.label}</span>}
              </NavLink>
            );
          }

          if (section.type === 'label') {
            if (collapsed) {
              return <div key={`label-${idx}`} className="mt-3 mb-1 mx-2 border-t border-border" />;
            }
            return (
              <div
                key={`label-${section.label}`}
                className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70"
              >
                {section.label}
              </div>
            );
          }

          if (section.type === 'group') {
            const Icon = section.icon;
            const open = openGroups[section.key] ?? false;
            return (
              <React.Fragment key={section.key}>
                <div className={`${collapsed ? 'mt-3 mb-1 mx-2' : 'mt-3 mb-1 mx-1'} border-t border-border`} />
                <Collapsible open={open} onOpenChange={(val) => setGroupOpen(section.key, val)}>
                  <CollapsibleTrigger asChild>
                    <button
                      className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3 justify-between'} px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors`}
                      title={collapsed ? section.label : undefined}
                    >
                      <span className={`flex items-center ${collapsed ? '' : 'gap-3'}`}>
                        <Icon className="h-4 w-4 shrink-0" />
                        {!collapsed && <span>{section.label}</span>}
                      </span>
                      {!collapsed && (open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
                    </button>
                  </CollapsibleTrigger>
                  {!collapsed && (
                    <CollapsibleContent>
                      <div className="ml-4 space-y-0.5 mt-0.5">
                        {section.items.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <NavLink key={item.path} to={item.path} className={subItemClass}>
                              {ItemIcon && <ItemIcon className="h-4 w-4 shrink-0" />}
                              <span className="text-sm">{item.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  )}
                </Collapsible>
              </React.Fragment>
            );
          }

          return null;
        })}
      </nav>

      {/* Bottom section */}
      <div className="px-2 pb-3 border-t border-border pt-3 space-y-1">
        {/* Sync Activity */}
        <NavLink
          to="/sync-activity"
          title={collapsed ? 'Sync Activity' : undefined}
          className={itemClass}
        >
          <Activity className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Sync Activity</span>}
        </NavLink>

        {/* Settings */}
        <NavLink
          to="/settings"
          title={collapsed ? 'Settings' : undefined}
          className={itemClass}
        >
          <Settings className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Settings</span>}
        </NavLink>

        {/* Dark mode toggle */}
        <Button
          variant="ghost"
          className={`w-full ${collapsed ? 'justify-center px-0' : 'justify-start gap-2'} text-muted-foreground`}
          onClick={() => setDarkMode((prev) => !prev)}
          title={darkMode ? 'Light mode' : 'Dark mode'}
        >
          {darkMode ? <Sun className="h-4 w-4 shrink-0" /> : <Moon className="h-4 w-4 shrink-0" />}
          {!collapsed && (darkMode ? 'Light mode' : 'Dark mode')}
        </Button>

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3'} rounded-lg px-3 py-2 text-sm font-medium transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2`}
              title={user?.displayName}
            >
              <Avatar className="h-8 w-8 shrink-0">
                <AvatarImage src={user?.clerkImageUrl || user?.profile?.avatar} alt={user?.displayName} />
                <AvatarFallback>{getInitials(user?.displayName)}</AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="flex flex-col items-start overflow-hidden">
                  <span className="text-foreground text-sm truncate">{user?.displayName}</span>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex items-center gap-2 text-muted-foreground">
              <User className="h-4 w-4" />
              <span>{user?.displayName}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => logout()} className="gap-2">
              <LogOut className="h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
